/**
 * <EmptyState/> — shared zero-data placeholder used across every list/table
 * surface in the app. Polished + on-brand alternative to a one-liner "No X yet".
 *
 * Usage:
 *   <EmptyState
 *     icon="phone"
 *     title="No calls yet"
 *     description="Place your first call from the dialer to get started."
 *     primaryAction={{ label: "Open dialer", onClick: openDialer }}
 *     secondaryAction={{ label: "Buy a number", onClick: goBuyNumber }}
 *     learnMore={{ label: "Read the docs", href: "/developers/docs/calls" }}
 *     testId="empty-state-calls"
 *   />
 *
 * Design contract:
 *  - left-aligned eyebrow + centered illustration cell (no AI-slop gradients)
 *  - one primary CTA, optional secondary CTA, optional "Learn more" link
 *  - data-testid on every interactive element + the container itself
 */
import React from "react";
import {
  Phone, ChatText, Users, Headphones, CalendarBlank,
  MicrophoneSlash, Hash, CreditCard, ChartBar, PuzzlePiece,
  AddressBook, CodeSimple, FileText, EnvelopeSimple,
} from "@phosphor-icons/react";

const ICONS = {
  phone: Phone,
  sms: ChatText,
  team: Users,
  recordings: Headphones,
  meetings: CalendarBlank,
  voicemail: MicrophoneSlash,
  numbers: Hash,
  billing: CreditCard,
  analytics: ChartBar,
  integrations: PuzzlePiece,
  contacts: AddressBook,
  developers: CodeSimple,
  reports: FileText,
  email: EnvelopeSimple,
};

export default function EmptyState({
  icon = "phone",
  title,
  description,
  primaryAction,
  secondaryAction,
  learnMore,
  className = "",
  compact = false,
  testId,
}) {
  const Icon = ICONS[icon] || Phone;
  return (
    <div
      className={`empty-state mx-auto max-w-xl ${compact ? "py-10" : "py-16"} px-6 text-center ${className}`}
      data-testid={testId || "empty-state"}
    >
      <div className="hx-empty__icon mx-auto mb-5 flex h-[52px] w-[52px] items-center justify-center">
        <Icon size={26} weight="duotone" />
      </div>
      <h3 className="text-base font-extrabold tracking-[-0.01em] text-[var(--text-primary)]" data-testid={`${testId || "empty-state"}-title`}>
        {title}
      </h3>
      {description && (
        <p className="mt-2 text-sm text-[var(--text-muted)] leading-relaxed" data-testid={`${testId || "empty-state"}-desc`}>
          {description}
        </p>
      )}
      {(primaryAction || secondaryAction) && (
        <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
          {primaryAction && (
            <button
              type="button"
              onClick={primaryAction.onClick}
              className="btn-primary"
              data-testid={`${testId || "empty-state"}-primary-btn`}
            >
              {primaryAction.label}
            </button>
          )}
          {secondaryAction && (
            <button
              type="button"
              onClick={secondaryAction.onClick}
              className="btn-ghost"
              data-testid={`${testId || "empty-state"}-secondary-btn`}
            >
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
      {learnMore && (
        <div className="mt-4">
          <a
            href={learnMore.href}
            target={learnMore.href?.startsWith("http") ? "_blank" : undefined}
            rel="noreferrer"
            className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors"
            data-testid={`${testId || "empty-state"}-learn-more`}
          >
            {learnMore.label} →
          </a>
        </div>
      )}
    </div>
  );
}
